import { ChangeEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import TextArea from 'react-textarea-autosize'
import { snakeCase } from 'lodash'
import * as Sentry from '@sentry/browser'

// Requests
import { submitContactHomebot } from 'src/api/mikasa/requests'

// Reducers
import {
  setContactHomebotStepAdvanceOneStep,
  selectContactHomebotStep,
  setContactHomebotShowModal,
  setContactHomebotStep,
  STEPS,
  STEPS_CONST,
  selectContactHomebotComments,
  setContactHomebotComments,
  selectContactHomebotEmail,
} from 'src/store/slices/contactHomebot'
import { selectListing } from 'src/store/selectors/listings'

// Components
import Step from '../../../../../ModalStepsOutline'
import { Text } from 'src/Components'
import { IconExclamationTriangle } from 'src/Components/shared/Icon'

// Styles
import styles from './Comments.module.scss'

export const Comments = () => {
  const dispatch = useDispatch()
  const intl = useIntl()

  const [isLoading, setIsLoading] = useState(false)
  const [isApiError, setIsApiError] = useState(false)

  const currentStep = useSelector(selectContactHomebotStep)
  const stepIndex = STEPS_CONST.indexOf(currentStep)

  const email = useSelector(selectContactHomebotEmail)
  const commentsText = useSelector(selectContactHomebotComments)
  const listing = useSelector(selectListing)

  const messageDefinitions = defineMessages({
    placeholder: {
      id: 'ContactHomebot.comments.placeholder.copy',
      defaultMessage:
        "For example: This listing is my house, and it doesn't have the correct amount of bathrooms",
    },
  })

  const handleSubmitButtonClick = async () => {
    setIsLoading(true)
    submitContactHomebot({
      clientEmail: email,
      message: commentsText,
      listingId: listing.id,
      listingProvider: snakeCase(listing.source?.name),
    })
      .then(() => {
        setIsLoading(false)
        dispatch(setContactHomebotStepAdvanceOneStep())
      })
      .catch(e => {
        setIsApiError(true)
        setIsLoading(false)
        Sentry.captureException(e)
      })
  }

  return (
    <Step stepLabel='contact-homebot-comments'>
      <Step.HeaderNavSection
        currentStep={currentStep}
        steps={STEPS}
        onCancel={() => dispatch(setContactHomebotShowModal(false))}
        onBack={() =>
          dispatch(setContactHomebotStep(STEPS_CONST[stepIndex - 1]))
        }
      >
        <FormattedMessage
          id='ContactHomebot.comments.last.step.title'
          defaultMessage='Last step'
        />
      </Step.HeaderNavSection>
      <Step.Title>
        <FormattedMessage
          id='ContactHomebot.comments.step.title'
          defaultMessage='Any comments?'
        />
      </Step.Title>
      <Step.Body>
        <div>
          <Text
            variant='neutralCool'
            variantCount='700'
            align='left'
            className={styles.subText}
          >
            <FormattedMessage
              id='ContactHomebot.comments.step.subtitle'
              defaultMessage='This will be sent directly to Homebot, and not your loan officer.'
            />
          </Text>
          <TextArea
            id='contact-homebot-comments'
            value={commentsText}
            onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
              if (e.target.value.length < 3000) {
                dispatch(setContactHomebotComments(e.target.value))
              }
            }}
            autoFocus
            minRows={6}
            maxRows={11}
            className={styles.textarea}
            placeholder={intl.formatMessage(messageDefinitions.placeholder)}
          />
        </div>
        {isApiError && (
          <div className={styles.errorState}>
            <div>
              <IconExclamationTriangle className={styles.triangle} />
            </div>
            <Text
              className={styles.typicalResponseTimeText}
              variant='warning'
              variantCount='600'
              align='center'
              element='h3'
              weight='semibold'
              size='xxs'
            >
              <FormattedMessage
                id='ScheduleTour.Questions.ApiError'
                defaultMessage="Sorry about this, but something didn't work on our end. Can you try again?"
              />
            </Text>
          </div>
        )}
      </Step.Body>
      <Step.FooterButton loading={isLoading} onClick={handleSubmitButtonClick}>
        <FormattedMessage
          id='ContactHomebot.comments.submit.button'
          defaultMessage='Submit'
        />
      </Step.FooterButton>
    </Step>
  )
}
